import React, { useState, useMemo } from "react";
import { DEFAULT_PAGE_SIZE, PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { fetchMyPermissions } from "../../store/slices/permissionsSlice";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useCreateRole,
  useAllRolesWithStats,
  useUpdateRole,
  useDeleteRole,
  useRole,
  useAllModules,
  useCreateModule,
  usePermissionMatrix,
} from "../../hooks/queries/useRBAC";
import { usePermissions } from "../../hooks/usePermissions";
import rbacService from "../../services/rbacService";
import {
  buildEnabledModulesFromRole,
  buildPermissionIdsForModuleVisibilitySave,
} from "../../utils/moduleVisibility";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Grid,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  Divider,
  Switch,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  Backdrop,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { DataGrid } from "@mui/x-data-grid";
import {
  Add,
  Edit,
  Delete,
  Search,
  Close,
  Security,
  Refresh,
  ContentCopy,
  Visibility,
} from "@mui/icons-material";

// Display-friendly action labels
const ACTION_TO_DISPLAY = {
  read: "View",
  create: "Create",
  update: "Edit",
  delete: "Delete",
  import: "Import",
  export: "Export",
  mark: "Mark",
  mark_score: "Grade",
  start: "Start",
  submit: "Submit",
  approve: "Approve",
  reject: "Reject",
  apply: "Apply",
  report: "Report",
  report_download: "Export Report",
  result_view: "View Result",
  result_download: "Download Result",
  request_edit: "Request Edit",
  request_change: "Request Change",
  info: "Info",
  download: "Download",
  mark_manual: "Mark Manual",
  one_to_one: "One-to-One",
  group: "Group",
  community_view: "Community",
  broadcast_view: "Broadcast",
  manage: "Manage",
  assign: "Assign",
  group_create_class: "Create Class Group",
  create_class: "Create Class",
  create_department: "Create Department",
  create_institute: "Create Institute",
  create_global: "Create Global",
  broadcast_create_department: "Broadcast Dept",
  broadcast_create_institute: "Broadcast Inst",
  link: "Link Device",
  read_student: "View Student Devices",
  read_staff: "View Staff Devices",
  approve_student: "Approve Student Device",
  approve_staff: "Approve Staff Device",
  reject_student: "Reject Student Device",
  reject_staff: "Reject Staff Device",
};

const RBAC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const basePath = pathname.replace(/\/rbac.*$/, "") || "/superadmin";
  const { user } = useSelector((s) => s.auth || {});
  const rawRole = user?.role?.slug || user?.role || "";
  const normalizedRole = String(rawRole || "").toLowerCase().trim();
  const isSuperAdminUser = normalizedRole === "superadmin" || normalizedRole === "super admin";
  const { hasPermission } = usePermissions();
  const canManageInstituteRbac =
    isSuperAdminUser || hasPermission("rbac.manage") || hasPermission("role.assign");
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("roles");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [creatingRole, setCreatingRole] = useState(false);
  const [togglingRoleId, setTogglingRoleId] = useState(null);
  const [restoringRoleId, setRestoringRoleId] = useState(null);
  const [savingEditRole, setSavingEditRole] = useState(false);

  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    enabledModules: {}, // { EXAM: true, TIMETABLE: false, ... }
  });

  const createRoleMutation = useCreateRole();
  const createModuleMutation = useCreateModule();
  const updateRoleMutation = useUpdateRole();
  const isUpdatingRole =
    savingEditRole || updateRoleMutation.isPending || updateRoleMutation.isLoading;
  const deleteRoleMutation = useDeleteRole();
  const dispatch = useDispatch();
  const { data: rolesData, isLoading: rolesLoading, refetch: refetchRoles } = useAllRolesWithStats();
  const { data: modulesRes, isLoading: modulesLoading } = useAllModules();
  const { refetch: refetchPermissionMatrix } = usePermissionMatrix();
  const [viewRoleId, setViewRoleId] = useState(null);
  const [editRoleId, setEditRoleId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, role: null });
  const [cloneRole, setCloneRole] = useState(null);
  const [editFormData, setEditFormData] = useState({ name: "", description: "", enabledModules: {} });
  const [viewModuleId, setViewModuleId] = useState(null);
  const [createModuleDialogOpen, setCreateModuleDialogOpen] = useState(false);
  const [createModuleForm, setCreateModuleForm] = useState({
    moduleName: "",
    displayName: "",
    actions: ["view", "create", "update", "delete"],
  });

  const rolesDataRes = rolesData?.data || rolesData;
  const rolesList = rolesDataRes?.roles || [];
  const summary = rolesDataRes?.summary || {};

  const roles = useMemo(() => {
    return rolesList.map((r) => ({
      id: r.id,
      name: r.displayName || r.name || r.role,
      code: (r.slug || r.role || "").toUpperCase().replace(/-/g, "_"),
      users: r.users ?? 0,
      modules: r.permissions ?? r.modules ?? 0,
      isSystem: r.isSystem ?? false,
      isCustom: r.isCustom ?? true,
      status: r.isActive === false ? "inactive" : "active",
      description: r.description || "",
      slug: r.slug || r.role,
      canDelete: r.canDelete ?? !r.isSystem,
      instituteId: r.instituteId ?? null,
      hasOverride: r.hasOverride === true,
    }));
  }, [rolesList]);

  const handleRestoreGlobal = async (role) => {
    try {
      if (!role?.id) return;
      setRestoringRoleId(role.id);
      await rbacService.syncGlobalRolePermissions(role.id);
      setSnackbar({ open: true, message: "Restored global permissions.", severity: "success" });
      try { await refetchRoles(); } catch (e) {}
      try { await refetchPermissionMatrix(); } catch (e) {}
      setTimeout(() => {
        try { dispatch(fetchMyPermissions()); } catch (e) {}
      }, 0);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || err?.message || "Failed to restore global permissions",
        severity: "error",
      });
    } finally {
      setRestoringRoleId(null);
    }
  };

  const filteredRoles = useMemo(() => {
    if (!searchQuery.trim()) return roles;
    const q = searchQuery.toLowerCase().trim();
    return roles.filter(
      (r) =>
        r.name?.toLowerCase().includes(q) ||
        r.code?.toLowerCase().includes(q) ||
        r.description?.toLowerCase().includes(q)
    );
  }, [roles, searchQuery]);

  const modules = useMemo(() => {
    const apiModules = modulesRes?.data || modulesRes || [];
    if (!Array.isArray(apiModules)) return [];
    return apiModules.map((m, idx) => {
      const code = (m.code || m.module || "").toUpperCase().replace(/-/g, "_");
      const perms = (m.permissions || []).map((p) => ({
        ...p,
        formKey: p.slug || p.action,
        displayLabel: ACTION_TO_DISPLAY[p.action] || (p.name || p.action || "").replace(/\b\w/g, (c) => c.toUpperCase()),
      }));
      // One row per permission slug (device has multiple read/approve/reject rows)
      const uniquePerms = perms.filter(
        (p, index, arr) => arr.findIndex((p2) => p2.slug === p.slug) === index
      );
      return {
        id: m.code || `mod-${idx}`,
        name: m.displayName || m.code || "",
        code,
        permissions: uniquePerms.map((p) => p.formKey),
        permissionLabels: uniquePerms.map((p) => p.displayLabel),
        permissionDetails: uniquePerms,
        description: `${m.displayName || m.code || ""} management`,
      };
    }).filter((m) => !["SUPERADMIN"].includes(m.code));
  }, [modulesRes]);

  const filteredModules = useMemo(() => {
    if (!searchQuery.trim()) return modules;
    const q = searchQuery.toLowerCase().trim();
    return modules.filter(
      (m) =>
        m.name?.toLowerCase().includes(q) ||
        m.code?.toLowerCase().includes(q) ||
        m.description?.toLowerCase().includes(q) ||
        m.permissions?.some((p) => String(p).toLowerCase().includes(q))
    );
  }, [modules, searchQuery]);

  const handleStatusToggle = async (role) => {
    if (role.isSystem) return;
    setTogglingRoleId(role.id);
    try {
      await updateRoleMutation.mutateAsync({
        id: role.id,
        roleData: { isActive: role.status !== "active" },
      });
      setSnackbar({ open: true, message: "Role status updated", severity: "success" });
      setTimeout(() => {
        try { dispatch(fetchMyPermissions()); } catch (e) {}
      }, 0);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Failed to update status",
        severity: "error",
      });
    } finally {
      setTogglingRoleId(null);
    }
  };

  const handleDeleteRole = async () => {
    if (!deleteConfirm.role) return;
    try {
      await deleteRoleMutation.mutateAsync(deleteConfirm.role.id);
      setSnackbar({ open: true, message: "Role deleted successfully", severity: "success" });
      setDeleteConfirm({ open: false, role: null });
      setTimeout(() => {
        try { dispatch(fetchMyPermissions()); } catch (e) {}
      }, 0);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || "Failed to delete role",
        severity: "error",
      });
    }
  };

  const handleCloneRole = (role) => {
    setCloneRole(role);
    setFormData({
      name: `Copy of ${role.name}`,
      description: role.description || "",
      enabledModules: {},
    });
    setCreateDialogOpen(true);
  };

  const roleIdToFetch = viewRoleId || editRoleId || cloneRole?.id;
  const { data: roleDetailRes, isLoading: roleDetailLoading } = useRole(roleIdToFetch);
  // API returns { status, message, data: role } - extract the role from nested data
  const roleDetail = roleDetailRes?.data?.data ?? roleDetailRes?.data ?? roleDetailRes;

  React.useEffect(() => {
    if (editRoleId && roleDetail && (!roleDetail.isSystem || isSuperAdminUser)) {
      const enabledModules = buildEnabledModulesFromRole(roleDetail, modules);
      setEditFormData({
        name: roleDetail.name || roleDetail.displayName || "",
        description: roleDetail.description || "",
        enabledModules,
      });
    }
  }, [editRoleId, roleDetail, modules, isSuperAdminUser]);

  React.useEffect(() => {
    if (cloneRole && roleDetail && createDialogOpen) {
      const enabledModules = buildEnabledModulesFromRole(roleDetail, modules);
      setFormData((prev) => ({
        ...prev,
        name: prev.name || `Copy of ${roleDetail.name || roleDetail.displayName || cloneRole.name}`,
        description: prev.description || roleDetail.description || cloneRole.description || "",
        enabledModules,
      }));
    }
  }, [cloneRole, roleDetail, createDialogOpen, modules]);

  const handleEditSubmit = async () => {
    if (!editRoleId || !roleDetail || isUpdatingRole) return;
    const existingRolePermissions = roleDetail.RolePermissions || roleDetail.role_permissions || [];
    const permissionIds = buildPermissionIdsForModuleVisibilitySave({
      modules,
      enabledModules: editFormData.enabledModules,
      existingRolePermissions,
      mode: "edit",
    });
    setSavingEditRole(true);
    try {
      const isInstituteEditor = !isSuperAdminUser && canManageInstituteRbac;
      const isSystemRole = !!roleDetail.isSystem;
      await updateRoleMutation.mutateAsync({
        id: editRoleId,
        roleData: isInstituteEditor
          ? { permissionIds }
          : isSystemRole
            ? { permissionIds }
            : {
                name: editFormData.name?.trim(),
                description: editFormData.description?.trim(),
                permissionIds,
              },
      });
      setSnackbar({ open: true, message: "Role updated successfully", severity: "success" });
      setEditRoleId(null);
      setTimeout(() => {
        try { dispatch(fetchMyPermissions()); } catch (e) {}
      }, 0);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || err?.message || "Failed to update role",
        severity: "error",
      });
    } finally {
      setSavingEditRole(false);
    }
  };

  const handleCreateSubmit = async () => {
    const permissionIds = buildPermissionIdsForModuleVisibilitySave({
      modules,
      enabledModules: formData.enabledModules,
      mode: "create",
    });
    const name = formData.name?.trim();
    if (!name) {
      setSnackbar({ open: true, message: "Role name is required", severity: "error" });
      return;
    }
    const slug =
      name.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "") || "custom_role";
    setCreatingRole(true);
    try {
      await createRoleMutation.mutateAsync({
        name,
        slug: cloneRole ? `${slug}_copy_${Date.now()}` : slug,
        description: formData.description?.trim() || undefined,
        hierarchyLevel: 0,
        permissionIds,
      });
      setFormData({ name: "", description: "", enabledModules: {} });
      setCloneRole(null);
      setTimeout(() => {
        try { dispatch(fetchMyPermissions()); } catch (e) {}
      }, 0);
      // Close dialog and show success toast
      setCreateDialogOpen(false);
      setTimeout(() => {
        setSnackbar({ open: true, message: "Role created successfully", severity: "success" });
      }, 100);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.message || err?.message || "Failed to create role",
        severity: "error",
      });
    } finally {
      setCreatingRole(false);
    }
  };

  const roleColumns = [
    {
      field: "name",
      headerName: "Role",
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar
            sx={{
              bgcolor: params.row.isSystem ? "primary.main" : "secondary.main",
              width: 40,
              height: 40,
            }}
          >
            <Security />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {params.row.name}
              {params.row.isSystem && (
                <Chip
                  label="System"
                  size="small"
                  sx={{ ml: 1, fontSize: 10 }}
                />
              )}
              {!params.row.isSystem && (
                <Chip
                  label={
                    params.row.instituteId
                      ? "Institute Custom"
                      : !isSuperAdminUser
                        ? params.row.hasOverride
                          ? "Customized"
                          : "Global"
                        : "Global"
                  }
                  size="small"
                  variant="outlined"
                  sx={{ ml: 1, fontSize: 10 }}
                />
              )}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {params.row.code}
            </Typography>
          </Box>
        </Box>
      ),
    },
    { field: "description", headerName: "Description", width: 180 },
    { field: "users", headerName: "Users", width: 80 },
    { field: "modules", headerName: "Modules", width: 80 },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Switch
            checked={params.value === "active"}
            size="small"
            disabled={params.row.isSystem || togglingRoleId === params.row.id}
            onChange={() => handleStatusToggle(params.row)}
          />
          <Chip
            label={params.value === "active" ? "Active" : "Inactive"}
            size="small"
            color={params.value === "active" ? "success" : "default"}
            variant={params.value === "active" ? "filled" : "outlined"}
            sx={{ height: 22, fontSize: 11 }}
          />
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View">
            <IconButton size="small" onClick={() => setViewRoleId(params.row.id)}>
              <Visibility />
            </IconButton>
          </Tooltip>
          {canManageInstituteRbac && (isSuperAdminUser || !params.row.isSystem) && (
            <Tooltip title={params.row.isSystem ? "Edit module access" : "Edit"}>
              <IconButton size="small" onClick={() => setEditRoleId(params.row.id)}>
                <Edit />
              </IconButton>
            </Tooltip>
          )}
          {!isSuperAdminUser && canManageInstituteRbac && params.row.hasOverride && (
            <Tooltip title="Restore Global Permissions">
              <IconButton
                size="small"
                onClick={() => handleRestoreGlobal(params.row)}
                disabled={restoringRoleId === params.row.id}
              >
                {restoringRoleId === params.row.id ? <CircularProgress size={18} /> : <Refresh />}
              </IconButton>
            </Tooltip>
          )}
          {!params.row.isSystem && (
            <Tooltip title="Clone Role">
              <IconButton size="small" onClick={() => handleCloneRole(params.row)}>
                <ContentCopy />
              </IconButton>
            </Tooltip>
          )}
          {params.row.canDelete && canManageInstituteRbac && (
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeleteConfirm({ open: true, role: params.row })}
              >
                <Delete />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  const moduleColumns = [
    {
      field: "name",
      headerName: "Module",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar
            sx={{ bgcolor: "info.light", width: 32, height: 32, fontSize: 14 }}
          >
            {(params.row.code || params.row.name || "?")[0]}
          </Avatar>
          <Typography variant="body2" fontWeight={500}>
            {params.value}
          </Typography>
        </Box>
      ),
    },
    { field: "code", headerName: "Code", width: 120 },
    { field: "description", headerName: "Description", flex: 1 },
    {
      field: "permissions",
      headerName: "Permissions",
      flex: 1,
      renderCell: (params) =>
        (params.row.permissionLabels || params.row.permissions || []).map((p, i) => (
          <Chip
            key={params.row.id + "-" + p + "-" + i}
            label={typeof p === "string" ? p : p.displayLabel || p}
            size="small"
            sx={{ mr: 0.5, textTransform: "capitalize" }}
          />
        )),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      renderCell: (params) => (
        <Box>
          <Tooltip title="View module details">
            <IconButton
              size="small"
              onClick={() => setViewModuleId(params.row.id)}
            >
              <Visibility />
            </IconButton>
          </Tooltip>
          <Tooltip title="Manage permissions for this module">
            <IconButton
              size="small"
              onClick={() => navigate(`${basePath}/roles-permissions`)}
            >
              <Edit />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Role-Based Access Control
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage roles, module visibility, and access levels
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {activeTab === "roles" && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Create Role
            </Button>
          )}
          {/* {activeTab === "modules" && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateModuleDialogOpen(true)}
              disabled={!isSuperAdminUser}
            >
              Create Module
            </Button>
          )} */}
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Action permissions (create, edit, delete, export, approve, etc.) are managed in{" "}
        <Button
          size="small"
          variant="text"
          sx={{ verticalAlign: "baseline", p: 0, minWidth: 0, textTransform: "none" }}
          onClick={() => navigate(`${basePath}/roles-permissions`)}
        >
          Roles &amp; Permissions
        </Button>
        . Use this page for role setup and to enable or disable whole modules (view access).
      </Alert>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {[
          { label: "Total Roles", value: summary.totalRoles ?? roles.length },
          {
            label: "System Roles",
            value: summary.systemRoles ?? roles.filter((r) => r.isSystem).length,
          },
          {
            label: "Custom Roles",
            value: summary.customRoles ?? roles.filter((r) => !r.isSystem).length,
          },
          { label: "Total Modules", value: summary.totalModules ?? modules?.length ?? 0 },
        ].map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card>
              <CardContent sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="h4" fontWeight={700}>
                  {stat.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.label}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs */}
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <Button
          variant={activeTab === "roles" ? "contained" : "outlined"}
          onClick={() => setActiveTab("roles")}
        >
          Roles
        </Button>
        <Button
          variant={activeTab === "modules" ? "contained" : "outlined"}
          onClick={() => setActiveTab("modules")}
        >
          Modules
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ mb: 2 }}>
            <TextField
              placeholder={activeTab === "roles" ? "Search roles..." : "Search modules..."}
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ width: 300 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {activeTab === "roles" && (
            <DataGrid
              rows={filteredRoles}
              columns={roleColumns}
              loading={rolesLoading}
              autoHeight
              disableRowSelectionOnClick
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              initialState={{
                pagination: { paginationModel: { pageSize: DEFAULT_PAGE_SIZE } },
              }}
              sx={{ border: "none" }}
            />
          )}
          {activeTab === "modules" && (
            <DataGrid
              rows={filteredModules}
              columns={moduleColumns}
              loading={modulesLoading}
              autoHeight
              disableRowSelectionOnClick
              pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              initialState={{
                pagination: { paginationModel: { pageSize: DEFAULT_PAGE_SIZE } },
              }}
              sx={{ border: "none" }}
            />
          )}
        </CardContent>
      </Card>

      {/* Create Role Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setCreateDialogOpen(false);
          setCloneRole(null);
          setFormData({ name: "", description: "", enabledModules: {} });
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            {cloneRole ? "Clone Role" : "Create New Role"}
            <IconButton onClick={() => setCreateDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Role Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Role Code"
                value={formData.name.toUpperCase().replace(/\s/g, "_")}
                disabled
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Enable modules to grant view access. Configure create, edit, delete, and other actions in{" "}
                <Button
                  size="small"
                  variant="text"
                  sx={{ verticalAlign: "baseline", p: 0, minWidth: 0, textTransform: "none" }}
                  onClick={() => navigate(`${basePath}/roles-permissions`)}
                >
                  Roles &amp; Permissions
                </Button>
                .
              </Alert>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                Module Visibility
              </Typography>
              {modules.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {modulesLoading ? "Loading modules…" : "No modules available. Seed default permissions first."}
                </Typography>
              )}
              {modules.map((module) => (
                <Accordion key={module.id || module.code} disableGutters elevation={0} sx={{ "&:before": { display: "none" } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
                      <Box>
                        <Typography fontWeight={500}>{module.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {module.code}
                        </Typography>
                      </Box>
                      <FormControlLabel
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                        control={
                          <Switch
                            checked={!!formData.enabledModules[module.code]}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                enabledModules: {
                                  ...formData.enabledModules,
                                  [module.code]: e.target.checked,
                                },
                              })
                            }
                          />
                        }
                        label="Enable"
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, color: "text.secondary", typography: "caption" }}>
                    Toggle module visibility for this role. Fine-grained actions are configured in the permission matrix after save.
                  </AccordionDetails>
                </Accordion>
              ))}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateSubmit}
            disabled={
              creatingRole ||
              createRoleMutation.isLoading ||
              (!!cloneRole && (!roleDetail || !roleDetail.RolePermissions?.length))
            }
          >
            {creatingRole || createRoleMutation.isLoading ? "Creating…" : cloneRole ? "Clone Role" : "Create Role"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Module Dialog */}
      <Dialog
        open={createModuleDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setCreateModuleDialogOpen(false);
          setCreateModuleForm({
            moduleName: "",
            displayName: "",
            actions: ["view", "create", "update", "delete"],
          });
        }}
        maxWidth="sm"
        fullWidth
      >
        {!isSuperAdminUser && (
          <Alert severity="info" sx={{ m: 2 }}>
            Module creation is managed at the global (Super Admin) level.
          </Alert>
        )}
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            Create New Module
            <IconButton onClick={() => setCreateModuleDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Module Name"
                value={createModuleForm.moduleName}
                onChange={(e) =>
                  setCreateModuleForm((prev) => ({
                    ...prev,
                    moduleName: e.target.value,
                  }))
                }
                placeholder="e.g., inventory, reports, analytics"
                helperText="This will be used as the module identifier (slugified)"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Display Name"
                value={createModuleForm.displayName}
                onChange={(e) =>
                  setCreateModuleForm((prev) => ({
                    ...prev,
                    displayName: e.target.value,
                  }))
                }
                placeholder="e.g., Inventory Management, Reports, Analytics"
                helperText="Human-readable name for the module"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Actions to create permissions for:
              </Typography>
              <Grid container spacing={1}>
                {["view", "create", "update", "delete", "export", "import", "manage"].map((action) => (
                  <Grid item key={action}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={createModuleForm.actions.includes(action)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setCreateModuleForm((prev) => ({
                              ...prev,
                              actions: checked
                                ? [...prev.actions, action]
                                : prev.actions.filter((a) => a !== action),
                            }));
                          }}
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
                          {action.replace("_", " ")}
                        </Typography>
                      }
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateModuleDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              createModuleMutation.mutate(createModuleForm, {
                onSuccess: (response) => {
                  setSnackbar({ open: true, message: "Module created successfully", severity: "success" });
                  setCreateModuleDialogOpen(false);
                  setCreateModuleForm({
                    moduleName: "",
                    displayName: "",
                    actions: ["view", "create", "update", "delete"],
                  });
                },
                onError: (error) => {
                  setSnackbar({
                    open: true,
                    message: error?.response?.data?.message || "Failed to create module",
                    severity: "error",
                  });
                },
              });
            }}
            disabled={!isSuperAdminUser || createModuleMutation.isLoading || !createModuleForm.moduleName.trim()}
          >
            {createModuleMutation.isLoading ? "Creating…" : "Create Module"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog
        open={!!editRoleId}
        onClose={(e, reason) => {
          if (isUpdatingRole) return;
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setEditRoleId(null);
          setEditFormData({ name: "", description: "", enabledModules: {} });
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              Edit Role
              {!isSuperAdminUser && roleDetail?.id && roles.find((r) => r.id === roleDetail.id)?.hasOverride && (
                <Chip label="CUSTOMIZED" size="small" color="primary" variant="outlined" />
              )}
            </Box>
            <IconButton onClick={() => setEditRoleId(null)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ position: "relative", minHeight: 120 }}>
         
          {roleDetailLoading && (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <CircularProgress size={32} sx={{ mb: 2 }} />
              <Typography color="text.secondary">Loading role details…</Typography>
            </Box>
          )}
          {!roleDetailLoading && roleDetail && (!roleDetail.isSystem || isSuperAdminUser) && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {roleDetail.isSystem && isSuperAdminUser && (
                <Grid item xs={12}>
                  <Alert severity="info">
                    System role: you can change which modules are enabled. Name and hierarchy cannot be changed here.
                  </Alert>
                </Grid>
              )}
              {!isSuperAdminUser && (
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 1 }}>
                    <Alert severity="info" sx={{ flex: 1 }}>
                      Institute admins can enable or disable modules for their institute. Action permissions are managed in Roles &amp; Permissions. Role name/description are global.
                    </Alert>
                    {roles.find((r) => r.id === roleDetail.id)?.hasOverride && (
                      <Button
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={() => handleRestoreGlobal(roles.find((r) => r.id === roleDetail.id))}
                      >
                        Restore Global
                      </Button>
                    )}
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Role Name"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  disabled={!isSuperAdminUser || roleDetail.isSystem}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Role Code" value={roleDetail.slug} disabled />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                  disabled={!isSuperAdminUser || roleDetail.isSystem}
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Action permissions are managed in{" "}
                  <Button
                    size="small"
                    variant="text"
                    sx={{ verticalAlign: "baseline", p: 0, minWidth: 0, textTransform: "none" }}
                    onClick={() => navigate(`${basePath}/roles-permissions`)}
                  >
                    Roles &amp; Permissions
                  </Button>
                  .
                </Alert>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  Module Visibility
                </Typography>
                {modules.map((module) => (
                  <Accordion key={module.code} disableGutters elevation={0} sx={{ "&:before": { display: "none" } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "space-between", pr: 1 }}>
                        <Box>
                          <Typography fontWeight={500}>{module.name}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {module.code}
                          </Typography>
                        </Box>
                        <FormControlLabel
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.stopPropagation()}
                          control={
                            <Switch
                              checked={!!editFormData.enabledModules[module.code]}
                              onChange={(e) =>
                                setEditFormData({
                                  ...editFormData,
                                  enabledModules: {
                                    ...editFormData.enabledModules,
                                    [module.code]: e.target.checked,
                                  },
                                })
                              }
                            />
                          }
                          label="Enable"
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0, color: "text.secondary", typography: "caption" }}>
                      Enable or disable this module for the role at your institute.
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRoleId(null)} disabled={isUpdatingRole}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleEditSubmit}
            disabled={isUpdatingRole || roleDetailLoading}
            startIcon={
              isUpdatingRole ? <CircularProgress size={18} color="inherit" /> : null
            }
          >
            {isUpdatingRole ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Role Dialog */}
      <Dialog open={!!viewRoleId} onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setViewRoleId(null); }} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            Role Details
            <IconButton onClick={() => setViewRoleId(null)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {roleDetailLoading && (
            <Box sx={{ py: 4, textAlign: "center" }}>
              <Typography color="text.secondary">Loading role details…</Typography>
            </Box>
          )}
          {!roleDetailLoading && roleDetail && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {roleDetail.name || roleDetail.displayName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Role Code
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {roleDetail.slug}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Description
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {roleDetail.description || "—"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Enabled Modules
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                {modules
                  .filter((m) => buildEnabledModulesFromRole(roleDetail, modules)[m.code])
                  .map((m) => (
                    <Chip key={m.code} label={m.name} size="small" variant="outlined" />
                  ))}
              </Box>
              {modules.filter((m) => buildEnabledModulesFromRole(roleDetail, modules)[m.code]).length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No modules enabled
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                Action permissions are configured in Roles &amp; Permissions.
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* View Module Dialog */}
      <Dialog open={!!viewModuleId} onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setViewModuleId(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            Module Details
            <IconButton onClick={() => setViewModuleId(null)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {viewModuleId && (() => {
            const viewModule = modules.find((m) => m.id === viewModuleId || m.code === viewModuleId);
            if (!viewModule) return null;
            return (
              <Box sx={{ pt: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Module Name
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {viewModule.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Code
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {viewModule.code}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {viewModule.description || "—"}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Permissions in this module
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                  {(viewModule.permissionLabels || viewModule.permissions || []).map((p, i) => (
                    <Chip
                      key={viewModule.id + "-" + i}
                      label={typeof p === "string" ? p : (p.displayLabel || p)}
                      size="small"
                      variant="outlined"
                      sx={{ textTransform: "capitalize" }}
                    />
                  ))}
                </Box>
              </Box>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirm.open} onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setDeleteConfirm({ open: false, role: null }); }}>
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          {deleteConfirm.role && (
            <Typography>
              Are you sure you want to delete &quot;{deleteConfirm.role.name}&quot;? This action cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm({ open: false, role: null })}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteRole}
            disabled={deleteRoleMutation.isLoading}
          >
            {deleteRoleMutation.isLoading ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RBAC;